import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional trailing count ("Staff 8"). Rendered muted + tabular. */
  count?: number;
  disabled?: boolean;
  title?: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: Array<SegmentedOption<T>>;
  ariaLabel: string;
  /**
   * `sm` (default) for inline toggles inside a card; `md` for the primary
   * filter bar of a list page, where the control is the page's main control.
   */
  size?: 'sm' | 'md';
  /**
   * Let the strip scroll sideways in one row instead of wrapping. Use on filter
   * bars with many options so a phone doesn't get two rows of chrome before the
   * first record.
   */
  scrollable?: boolean;
  className?: string;
}

/**
 * The one filter-toggle strip: All / Billable / Non-billable, Everyone / Staff /
 * Contractors, Draft / Open / Late, Active / Archived / All. Every list page
 * uses this so the same idea — "narrow this list to one slice" — looks and
 * behaves the same everywhere instead of shipping a white-on-grey track on one
 * screen and a bordered white pill on the next.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = 'sm',
  scrollable = false,
  className,
}: SegmentedControlProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  // The fade only earns its place when something is actually hidden to the
  // right. Painted unconditionally it dims the last option of a strip that
  // fits, which reads as a clipped label rather than as "there is more".
  const [hasMore, setHasMore] = useState(false);
  /** …and something hidden to the LEFT, once the reader has scrolled. */
  const [hasLess, setHasLess] = useState(false);
  useEffect(() => {
    const el = trackRef.current;
    if (!scrollable || !el) return;
    const update = () => {
      setHasMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 2);
      setHasLess(el.scrollLeft > 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    /**
     * ⚠ Observe the OPTIONS too, not just the track.
     *
     * The track is `w-full`, so its own border box never changes when the
     * webfont swaps in — only the buttons inside get wider. Observing the track
     * alone therefore measured `scrollWidth` once, in the fallback font, decided
     * nothing was hidden, and never looked again: the Approvals filter bar
     * clipped "Not submit…" mid-word at 390px with no fade and no hint that the
     * Approved filter existed at all. Fonts get an explicit re-check as well,
     * because a swap that changes no element's box is not a resize event.
     */
    for (const child of Array.from(el.children)) observer.observe(child);
    document.fonts?.ready.then(update).catch(() => {});
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [scrollable, options.length]);

  const group = (
    <div
      ref={trackRef}
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center rounded-lg border border-grey-200 bg-white p-0.5',
        scrollable
          ? 'w-full max-w-full overflow-x-auto scroll-smooth sm:w-auto sm:overflow-visible'
          : className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          disabled={option.disabled}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={cn(
            'shrink-0 whitespace-nowrap rounded-md font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
            size === 'md' ? 'px-3 py-1.5 text-body-sm' : 'px-2.5 py-1 text-xs',
            'disabled:cursor-not-allowed disabled:opacity-40',
            value === option.value
              ? 'bg-secondary text-white'
              : 'text-grey-500 hover:bg-grey-100 hover:text-secondary',
          )}
        >
          {option.label}
          {option.count !== undefined && (
            <span className="num ml-1.5 opacity-70">{option.count}</span>
          )}
        </button>
      ))}
    </div>
  );

  if (!scrollable) return group;

  /**
   * A strip that scrolls has to LOOK like it scrolls.
   *
   * The fade alone was not enough: at 390px the Approvals filter clipped "Not
   * submit…" mid-word and a whole option ("Approved") sat off-screen on a strip
   * that read as finished — a gradient says "this text is fading", not "there
   * is another control this way". So the fade now carries a chevron, and a
   * matching one appears on the left once you have scrolled, which is the pair
   * every scrollable tab strip uses. Both are pointer-transparent: they are
   * signposts, not buttons, and the strip itself takes the swipe.
   */
  return (
    <div className={cn('relative inline-block max-w-full align-top', className)}>
      {group}
      {hasLess && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-px left-px flex w-8 items-center justify-start rounded-l-lg bg-gradient-to-r from-white via-white/90 to-transparent pl-0.5 text-grey-400 sm:hidden"
        >
          <ChevronRight className="h-3.5 w-3.5 rotate-180" />
        </span>
      )}
      {hasMore && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-px right-px flex w-8 items-center justify-end rounded-r-lg bg-gradient-to-l from-white via-white/90 to-transparent pr-0.5 text-grey-400 sm:hidden"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}
